import { HttpInterceptorFn } from '@angular/common/http';

/** Nombre de la cookie que el backend (Laravel/Sanctum) usa para publicar el token CSRF. */
const XSRF_COOKIE_NAME = 'XSRF-TOKEN';

/** Nombre fijo del header esperado por el backend. NO es el nombre de la cookie. */
const XSRF_HEADER_NAME = 'X-XSRF-TOKEN';

/**
 * Lee y decodifica el valor de una cookie por nombre desde `document.cookie`.
 *
 * Importante: aplica `decodeURIComponent` porque el backend (Laravel/Sanctum)
 * espera recibir el header `X-XSRF-TOKEN` con el valor DECODIFICADO del token
 * (así lo hace el propio interceptor XSRF por defecto de Angular/Axios). Si se
 * envía el valor todavía URL-encoded tal cual llega en la cookie, `hash_equals`
 * falla en el backend contra `$request->session()->token()` y devuelve 419.
 */
function readDecodedCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }

  const prefix = `${name}=`;
  const cookies = document.cookie.split('; ');

  for (const cookie of cookies) {
    if (cookie.startsWith(prefix)) {
      return decodeURIComponent(cookie.slice(prefix.length));
    }
  }

  return null;
}

/**
 * Añade el header `X-XSRF-TOKEN` con el valor DECODIFICADO de la cookie
 * `XSRF-TOKEN` en todas las peticiones al backend, y fuerza
 * `withCredentials: true` para que el navegador adjunte también la cookie de
 * sesión HttpOnly (`fichanet_dev_session`).
 *
 * El token CSRF está atado a esa sesión en el servidor: si se envía el header
 * sin la cookie de sesión correspondiente, `hash_equals` fallará en el
 * backend aunque el token en sí sea correcto.
 */
export const xsrfInterceptor: HttpInterceptorFn = (req, next) => {
  let xsrfReq = req.withCredentials ? req : req.clone({ withCredentials: true });

  const token = readDecodedCookie(XSRF_COOKIE_NAME);
  if (token) {
    xsrfReq = xsrfReq.clone({
      headers: xsrfReq.headers.set(XSRF_HEADER_NAME, token),
    });
  }

  return next(xsrfReq);
};
